import React, { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Divider,
  Link,
  Chip,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  ArrowForward,
  OpenInNew,
  ArrowBack,
  AccessTime,
  CalendarToday,
  Person,
  LinkedIn,
} from '@mui/icons-material';
import { Doughnut } from 'react-chartjs-2';
import { Chart, registerables, ArcElement } from 'chart.js';
import SEOHead from '../../components/SEO/SEOHead';
import BreadcrumbNav from '../../components/SEO/BreadcrumbNav';
import AtomicAnswer from '../../components/SEO/AtomicAnswer';
import FAQSection from '../../components/SEO/FAQSection';
import NewsletterSection from '../../components/Layout/NewsletterSection';
import TrustStrip from '../../components/Layout/TrustStrip';
import ArticleTable from '../../components/Blog/ArticleTable';
import RelatedArticles from '../../components/Blog/RelatedArticles';
import carlosPhoto from '../../assets/img/author-carlos.jpg';
import nataliaPhoto from '../../assets/img/author-natalia.jpg';
import { useTranslation } from 'react-i18next';
import { REFERRALS } from '../../components/shared/ReferralCTA';

Chart.register(...registerables, ArcElement);

const EXPENSE_CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: true,
  plugins: {
    legend: {
      position: 'right',
      labels: {
        padding: 12,
        usePointStyle: true,
        pointStyle: 'circle',
        font: { size: 12, family: 'Inter' },
        color: '#1A1A1A',
        generateLabels: (chart) => {
          const data = chart.data;
          const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
          return data.labels.map((label, i) => ({
            text: `${label} · $${data.datasets[0].data[i]} (${Math.round((data.datasets[0].data[i] / total) * 100)}%)`,
            fillStyle: data.datasets[0].backgroundColor[i],
            strokeStyle: '#fff',
            lineWidth: 2,
            pointStyle: 'circle',
            index: i,
          }));
        },
      },
    },
    tooltip: {
      callbacks: {
        label: (ctx) => {
          const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
          const pct = Math.round((ctx.raw / total) * 100);
          return ` $${ctx.raw} CAD/mes (${pct}%)`;
        },
      },
    },
  },
  cutout: '60%',
};

const CostoVidaMontrealPage = () => {
  const { t, i18n } = useTranslation();
  const { lng } = useParams();
  const [takeawaysOpen, setTakeawaysOpen] = useState(false);

  const p = 'blog.costoVidaMontreal';

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: t(`${p}.seoTitle`),
    description: t(`${p}.seoDescription`),
    datePublished: '2026-03-02',
    dateModified: '2026-03-02',
    author: {
      '@type': 'Person',
      name: 'Carlos Alviz',
      url: 'https://www.finmaple.com/es/sobre-nosotros',
    },
    editor: {
      '@type': 'Person',
      name: 'Natalia Duarte',
      url: 'https://www.finmaple.com/es/sobre-nosotros',
    },
    publisher: {
      '@type': 'Organization',
      name: 'FinMaple',
      url: 'https://www.finmaple.com',
      logo: { '@type': 'ImageObject', url: 'https://www.finmaple.com/og-image.jpg' },
    },
    image: 'https://www.finmaple.com/images/blog/costo-vida-montreal-2026.jpg',
    mainEntityOfPage: 'https://www.finmaple.com/es/blog/costo-de-vida-montreal-2026',
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.article-headline', '.atomic-answer'],
    },
    inLanguage: i18n.language,
  };

  const TAGS = [
    t(`${p}.tag1`),
    t(`${p}.tag2`),
    t(`${p}.tag3`),
    t(`${p}.tag4`),
  ];

  const MONTHLY_EXPENSES = [
    { category: t(`${p}.expCatRent`), range: '$1,200 – $1,600' },
    { category: t(`${p}.expCatFood`), range: '$350 – $500' },
    { category: t(`${p}.expCatTransport`), range: '$100' },
    { category: t(`${p}.expCatUtilities`), range: '$180 – $250' },
    { category: t(`${p}.expCatHealth`), range: '$0 – $80' },
    { category: t(`${p}.expCatClothing`), range: '$50 – $100' },
    { category: t(`${p}.expCatEntertainment`), range: '$50 – $150' },
    { category: t(`${p}.expCatSavings`), range: '$100 – $200' },
  ];

  const MONTREAL_VS_TORONTO = [
    { concept: t(`${p}.vsCatRent`), montreal: '$1,400', toronto: '$2,200' },
    { concept: t(`${p}.vsCatTransit`), montreal: '$100', toronto: '$156' },
    { concept: t(`${p}.vsCatDaycare`), montreal: '$192*', toronto: '$1,200 – $1,800' },
    { concept: t(`${p}.vsCatElectricity`), montreal: '$80 – $120', toronto: '$120 – $180' },
    { concept: t(`${p}.vsCatGrocery`), montreal: '$350 – $500', toronto: '$400 – $550' },
    { concept: t(`${p}.vsCatMinWage`), montreal: '$16.10', toronto: '$17.20' },
  ];

  const EXPENSE_CHART_DATA = {
    labels: [
      t(`${p}.chartRent`), t(`${p}.chartFood`), t(`${p}.chartTransport`),
      t(`${p}.chartUtilities`), t(`${p}.chartHealth`), t(`${p}.chartClothing`),
      t(`${p}.chartEntertainment`), t(`${p}.chartSavings`),
    ],
    datasets: [{
      data: [1400, 425, 100, 215, 40, 75, 100, 150],
      backgroundColor: [
        '#1E3A5F', '#2E7D32', '#F9A825', '#5F6B7A',
        '#D32F2F', '#7B1FA2', '#00838F', '#EF6C00',
      ],
      borderWidth: 2,
      borderColor: '#fff',
    }],
  };

  const FAQS = [
    { question: t(`${p}.faq1Q`), answer: t(`${p}.faq1A`) },
    { question: t(`${p}.faq2Q`), answer: t(`${p}.faq2A`) },
    { question: t(`${p}.faq3Q`), answer: t(`${p}.faq3A`) },
  ];

  const AUTHOR = { name: 'Carlos Alviz', credential: 'B.B.A. (UQAM)', role: t(`${p}.authorRole`), linkedin: 'https://www.linkedin.com/in/alvizcarlos/', photo: carlosPhoto };
  const EDITOR = { name: 'Natalia Duarte', credential: 'B.B.A.', role: t(`${p}.editorRole`), linkedin: 'https://www.linkedin.com/in/natalia-duarteh/', photo: nataliaPhoto };
  const HERO_IMAGE = '/images/blog/costo-vida-montreal-2026.jpg';

  return (
    <Box component="article" sx={{ bgcolor: '#F7F9FC' }}>
      <SEOHead
        title={t(`${p}.seoTitle`)}
        description={t(`${p}.seoDescription`)}
        canonical="/blog/costo-de-vida-montreal-2026"
        routeKey="blogCostoVida"
        seoKey="blogCostoVida"
        noIndex={i18n.language !== 'es'}
        schema={articleSchema}
        ogImage="https://www.finmaple.com/images/blog/costo-vida-montreal-2026.jpg"
      />

      {/* Hero Image */}
      <Box
        role="img"
        aria-label={t(`${p}.heroAria`)}
        sx={{
          width: '100%',
          height: { xs: 220, md: 360 },
          backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.45)), url(${HERO_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center 30%',
          display: 'flex',
          alignItems: 'flex-end',
        }}
      >
        <Container maxWidth="md" sx={{ pb: 4 }}>
          <Button
            component={RouterLink}
            to={`/${lng}/blog`}
            startIcon={<ArrowBack />}
            sx={{ color: '#fff', textTransform: 'none', mb: 2, fontWeight: 500 }}
          >
            {t(`${p}.backToGuides`)}
          </Button>
        </Container>
      </Box>

      <TrustStrip />

      <Container maxWidth="md" sx={{ mt: 0, position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '14px',
            px: { xs: 3, md: 5 },
            py: { xs: 3, md: 4 },
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          }}
        >
          <BreadcrumbNav items={[{ label: 'Blog', to: `/${lng}/blog` }, { label: t(`${p}.breadcrumb`) }]} />

          {/* Tags */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {TAGS.map((tag, i) => (
              <Chip key={i} label={tag} size="small" sx={{ bgcolor: '#F0F2F5', color: '#5F6B7A', fontWeight: 500, fontSize: '0.8rem' }} />
            ))}
          </Box>

          {/* Title */}
          <Typography variant="h3" component="h1" className="article-headline" sx={{ fontWeight: 600, color: '#1A1A1A', lineHeight: 1.2, mb: 3, fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
            {t(`${p}.title`)}
          </Typography>

          {/* Meta */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, color: '#5F6B7A', mb: 3, fontSize: '0.9rem' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Person sx={{ fontSize: 18 }} />
              <a href={AUTHOR.linkedin} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#1E3A5F', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E3A5F' }}>{AUTHOR.name}</Typography>
                <LinkedIn sx={{ fontSize: 16, color: '#1E3A5F' }} />
              </a>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarToday sx={{ fontSize: 16 }} />
              <Typography variant="body2">{t(`${p}.date`)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTime sx={{ fontSize: 16 }} />
              <Typography variant="body2">{t(`${p}.readTime`)}</Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Atomic Answer */}
          <AtomicAnswer text={t(`${p}.atomicAnswer`)} />

          {/* Key Takeaways */}
          <Box
            sx={{
              border: '1px solid #EEF0F3',
              borderRadius: '10px',
              overflow: 'hidden',
              mt: 5,
              mb: 5,
            }}
          >
            <Box
              onClick={() => setTakeawaysOpen(!takeawaysOpen)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 1.5,
                bgcolor: '#F7F9FC',
                cursor: 'pointer',
                '&:hover': { bgcolor: '#EEEEEE' },
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                {t(`${p}.keyTakeaways`)}
              </Typography>
              {takeawaysOpen ? <ExpandLess sx={{ color: '#5F6B7A' }} /> : <ExpandMore sx={{ color: '#5F6B7A' }} />}
            </Box>
            <Box component="ul" sx={{ display: takeawaysOpen ? 'block' : 'none', px: 3, py: 2, m: 0, '& li': { mb: 1, color: '#1A1A1A', lineHeight: 1.6 } }}>
                <li>{t(`${p}.takeaway1`)}</li>
                <li>{t(`${p}.takeaway2`)}</li>
                <li>{t(`${p}.takeaway3`)}</li>
                <li>{t(`${p}.takeaway4`)}</li>
                <li>{t(`${p}.takeaway5`)}</li>
              </Box>
          </Box>

          {/* ───────────────────────────────────────────────────────── */}
          {/* H2: Desglose mensual */}
          {/* ───────────────────────────────────────────────────────── */}
          <Typography variant="h4" component="h2" sx={{ color: '#1A1A1A', mt: 6, mb: 3 }}>
            {t(`${p}.h2Breakdown`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.breakdownIntro`)}
          </Typography>

          <ArticleTable
            columns={[
              { key: 'category', label: t(`${p}.colCategory`) },
              { key: 'range', label: t(`${p}.colRange`) },
            ]}
            rows={MONTHLY_EXPENSES}
            footer={[{ category: t(`${p}.totalEstimated`), range: '$2,027 – $2,877' }]}
          />

          {/* Doughnut chart — visual anchor */}
          <Box sx={{ bgcolor: '#FAFBFD', border: '1px solid #EEF0F3', borderRadius: '14px', p: { xs: 2, md: 4 }, mb: 4, textAlign: 'center' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
              {t(`${p}.chartTitle`)}
            </Typography>
            <Typography variant="caption" sx={{ color: '#5F6B7A', display: 'block', mb: 2 }}>
              {t(`${p}.chartSubtitle`)}
            </Typography>
            <Box sx={{ maxWidth: 480, mx: 'auto' }}>
              <Doughnut data={EXPENSE_CHART_DATA} options={EXPENSE_CHART_OPTIONS} />
            </Box>
          </Box>

          <Box sx={{ bgcolor: '#F7F9FC', borderLeft: '4px solid #1E3A5F', p: 3, borderRadius: '0 8px 8px 0', mb: 3 }}>
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
              <strong>{t(`${p}.noteLabel`)}</strong> {t(`${p}.noteCouple`)}
            </Typography>
          </Box>

          {/* ───────────────────────────────────────────────────────── */}
          {/* H2: Vivienda y alquiler */}
          {/* ───────────────────────────────────────────────────────── */}
          <Typography variant="h4" component="h2" sx={{ color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t(`${p}.h2Housing`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.housingIntro`)}
          </Typography>

          <Typography variant="body1" component="h3" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 3, mb: 2 }}>
            {t(`${p}.h3RentAvg`)}
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 1.5, color: '#1A1A1A', lineHeight: 1.7 } }}>
            <li><strong>{t(`${p}.rentStudio`)}</strong> {t(`${p}.rentStudioDesc`)}</li>
            <li><strong>{t(`${p}.rent1bed`)}</strong> {t(`${p}.rent1bedDesc`)}</li>
            <li><strong>{t(`${p}.rent2bed`)}</strong> {t(`${p}.rent2bedDesc`)}</li>
            <li><strong>{t(`${p}.rent3bed`)}</strong> {t(`${p}.rent3bedDesc`)}</li>
          </Box>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.cheapNeighborhoods`)}
          </Typography>

          {/* Warning box */}
          <Box sx={{ bgcolor: '#FFFBF0', borderLeft: '4px solid #F9A825', p: 3, borderRadius: '0 8px 8px 0', mb: 2.5 }}>
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
              <strong>{t(`${p}.keyFactLabel`)}</strong> {t(`${p}.talRentControl`)}
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.housingLinkPre`)}
            <Link component={RouterLink} to={`/${lng}/hipotecas`} sx={{ color: '#1E3A5F', fontWeight: 600 }}>
              {t(`${p}.housingLinkMortgage`)}
            </Link>
            {t(`${p}.housingLinkMid`)}
            <RouterLink to={`/${lng}/blog/primera-hipoteca-canada-guia`} style={{ color: '#1E3A5F', fontWeight: 600, textDecoration: 'underline' }}>{t(`${p}.housingLinkFirstMortgage`)}</RouterLink>
            {t(`${p}.housingLinkPost`)}
          </Typography>

          {/* ───────────────────────────────────────────────────────── */}
          {/* H2: Alimentación */}
          {/* ───────────────────────────────────────────────────────── */}
          <Typography variant="h4" component="h2" sx={{ color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t(`${p}.h2Food`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.foodIntro`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.foodStores`)}
          </Typography>
          <Box sx={{ bgcolor: '#F7F9FC', borderLeft: '4px solid #1E3A5F', p: 3, borderRadius: '0 8px 8px 0', mb: 3 }}>
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
              <strong>{t(`${p}.savingTipLabel`)}</strong> {t(`${p}.savingTipFlipp`)}
            </Typography>
          </Box>

          {/* ───────────────────────────────────────────────────────── */}
          {/* H2: Transporte */}
          {/* ───────────────────────────────────────────────────────── */}
          <Typography variant="h4" component="h2" sx={{ color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t(`${p}.h2Transport`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.transportIntro`)}
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 1.5, color: '#1A1A1A', lineHeight: 1.7 } }}>
            <li><strong>{t(`${p}.transitMonthly`)}</strong> {t(`${p}.transitMonthlyPrice`)}</li>
            <li><strong>{t(`${p}.transitReduced`)}</strong> {t(`${p}.transitReducedPrice`)}</li>
            <li><strong>{t(`${p}.transitSingle`)}</strong> {t(`${p}.transitSinglePrice`)}</li>
            <li><strong>{t(`${p}.transitWeekly`)}</strong> {t(`${p}.transitWeeklyPrice`)}</li>
          </Box>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.transportSuburbs`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.transportCar`)}
          </Typography>

          {/* ───────────────────────────────────────────────────────── */}
          {/* H2: Servicios básicos */}
          {/* ───────────────────────────────────────────────────────── */}
          <Typography variant="h4" component="h2" sx={{ color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t(`${p}.h2Utilities`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.utilitiesIntro`)}
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 1.5, color: '#1A1A1A', lineHeight: 1.7 } }}>
            <li><strong>{t(`${p}.utilHydro`)}</strong> {t(`${p}.utilHydroDesc`)}</li>
            <li><strong>{t(`${p}.utilInternet`)}</strong> {t(`${p}.utilInternetDesc`)}</li>
            <li><strong>{t(`${p}.utilPhone`)}</strong> {t(`${p}.utilPhoneDesc`)}</li>
          </Box>

          <Box sx={{ bgcolor: '#F7F9FC', borderLeft: '4px solid #1E3A5F', p: 3, borderRadius: '0 8px 8px 0', mb: 3 }}>
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
              <strong>{t(`${p}.importantFactLabel`)}</strong> {t(`${p}.heatingIncluded`)}
            </Typography>
          </Box>

          {/* ───────────────────────────────────────────────────────── */}
          {/* H2: Salud */}
          {/* ───────────────────────────────────────────────────────── */}
          <Typography variant="h4" component="h2" sx={{ color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t(`${p}.h2Health`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.healthIntro`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.healthNotCovered`)}
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1, color: '#1A1A1A', lineHeight: 1.7 } }}>
            <li>{t(`${p}.healthDental`)}</li>
            <li>{t(`${p}.healthVision`)}</li>
            <li>{t(`${p}.healthMeds`)}</li>
            <li>{t(`${p}.healthTherapy`)}</li>
          </Box>

          {/* Warning box */}
          <Box sx={{ bgcolor: '#FFFBF0', borderLeft: '4px solid #F9A825', p: 3, borderRadius: '0 8px 8px 0', mb: 3 }}>
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
              <strong>{t(`${p}.ramqWaitLabel`)}</strong> {t(`${p}.ramqWaitDesc`)}
            </Typography>
          </Box>

          {/* ───────────────────────────────────────────────────────── */}
          {/* H2: Cuánto necesitas ganar */}
          {/* ───────────────────────────────────────────────────────── */}
          <Typography variant="h4" component="h2" sx={{ color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t(`${p}.h2Salary`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.salaryIntro`)}
          </Typography>

          <Box sx={{ bgcolor: '#F7F9FC', borderLeft: '4px solid #1E3A5F', p: 3, borderRadius: '0 8px 8px 0', mb: 3 }}>
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
              <strong>{t(`${p}.referenceLabel`)}</strong> {t(`${p}.minWageCalc`)}
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.salaryLinkPre`)}
            <Link component={RouterLink} to={`/${lng}/empieza-aqui`} sx={{ color: '#1E3A5F', fontWeight: 600 }}>
              {t(`${p}.salaryLinkGuide`)}
            </Link>
            {t(`${p}.salaryLinkMid`)}
            <RouterLink to={`/${lng}/blog/construir-credito-canada-desde-cero`} style={{ color: '#1E3A5F', fontWeight: 600, textDecoration: 'underline' }}>{t(`${p}.salaryLinkCredit`)}</RouterLink>
            {t(`${p}.salaryLinkPost`)}
          </Typography>

          {/* ───────────────────────────────────────────────────────── */}
          {/* H2: Montreal vs Toronto */}
          {/* ───────────────────────────────────────────────────────── */}
          <Typography variant="h4" component="h2" sx={{ color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t(`${p}.h2VsToronto`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.vsIntro`)}
          </Typography>

          <ArticleTable
            columns={[
              { key: 'concept', label: t(`${p}.colConcept`) },
              { key: 'montreal', label: 'Montreal' },
              { key: 'toronto', label: 'Toronto' },
            ]}
            rows={MONTREAL_VS_TORONTO.map((row) => ({
              concept: row.concept,
              montreal: { text: row.montreal, sx: { color: '#2E7D32', fontWeight: 600 } },
              toronto: row.toronto,
            }))}
          />

          <Typography variant="caption" sx={{ color: '#5F6B7A', display: 'block', mb: 3 }}>
            {t(`${p}.daycareFootnote`)}
          </Typography>

          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.vsRentDiff`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.vsFrenchReq`)}
          </Typography>

          {/* ───────────────────────────────────────────────────────── */}
          {/* H2: Consejos para ahorrar */}
          {/* ───────────────────────────────────────────────────────── */}
          <Typography variant="h4" component="h2" sx={{ color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t(`${p}.h2Tips`)}
          </Typography>
          <Box component="ol" sx={{ pl: 3, mb: 3, '& li': { mb: 2, color: '#1A1A1A', lineHeight: 1.7 } }}>
            <li>
              <strong>{t(`${p}.tip1Title`)}</strong> {t(`${p}.tip1Pre`)}
              <a
                href={REFERRALS.tangerine.href}
                target="_blank"
                rel="noopener noreferrer sponsored"
                style={{ color: '#1E3A5F', fontWeight: 600, textDecoration: 'underline' }}
                onClick={() => { if (window.plausible) window.plausible('Referral Click', { props: { bank: 'tangerine' } }); }}
              >Tangerine</a>
              {' ' + t(`${p}.tip1Or`) + ' '}
              <a
                href={REFERRALS.wealthsimple.href}
                target="_blank"
                rel="noopener noreferrer sponsored"
                style={{ color: '#1E3A5F', fontWeight: 600, textDecoration: 'underline' }}
                onClick={() => { if (window.plausible) window.plausible('Referral Click', { props: { bank: 'wealthsimple' } }); }}
              >Wealthsimple</a>
              {'. '}
              {t(`${p}.tip1Post`)} <RouterLink to={`/${lng}/blog/mejores-cuentas-sin-fees-recien-llegados`} style={{ color: '#1E3A5F', fontWeight: 600, textDecoration: 'underline' }}>{t(`${p}.tip1Link`)}</RouterLink>.
            </li>
            <li>
              <strong>{t(`${p}.tip2Title`)}</strong> {t(`${p}.tip2Desc`)}
            </li>
            <li>
              <strong>{t(`${p}.tip3Title`)}</strong>
              {' '}
              {t(`${p}.tip3Pre`)}
              <Link component={RouterLink} to={`/${lng}/tarjetas-de-credito`} sx={{ color: '#1E3A5F', fontWeight: 600 }}>
                {t(`${p}.tip3Link`)}
              </Link>
              {t(`${p}.tip3Post`)}
            </li>
            <li>
              <strong>{t(`${p}.tip4Title`)}</strong> {t(`${p}.tip4Desc`)}
            </li>
            <li>
              <strong>{t(`${p}.tip5Title`)}</strong> {t(`${p}.tip5Pre`)} <RouterLink to={`/${lng}/blog/tfsa-vs-rrsp-vs-fhsa-inmigrantes`} style={{ color: '#1E3A5F', fontWeight: 600, textDecoration: 'underline' }}>{t(`${p}.tip5Link`)}</RouterLink>.
            </li>
            <li>
              <strong>{t(`${p}.tip6Title`)}</strong> {t(`${p}.tip6Desc`)}
            </li>
          </Box>

          {/* Experience Panel */}
          <Box sx={{ bgcolor: '#F0FAF1', borderLeft: '4px solid #2E7D32', p: 3, borderRadius: '0 8px 8px 0', my: 4 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 1 }}>
              {t(`${p}.experienceTitle`)}
            </Typography>
            <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, fontStyle: 'italic' }}>
              {t(`${p}.experienceText`)}
            </Typography>
          </Box>

          <Divider sx={{ mt: 5, mb: 0 }} />

          {/* FAQ Section */}
          <FAQSection faqs={FAQS} />


          {/* Interlink CTA Box */}
          <Box
            sx={{
              bgcolor: '#1E3A5F',
              borderRadius: '10px',
              p: 3,
              mt: 4,
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600, mb: 0.5 }}>
                {t(`${p}.ctaTitle`)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
                {t(`${p}.ctaSubtitle`)}
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to={`/${lng}/empieza-aqui`}
              variant="contained"
              endIcon={<ArrowForward />}
              sx={{
                bgcolor: '#fff',
                color: '#1E3A5F',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '14px',
                px: 3,
                flexShrink: 0,
                '&:hover': { bgcolor: '#E8EAF6' },
              }}
            >
              {t(`${p}.ctaButton`)}
            </Button>
          </Box>

          {/* Official Sources */}
          <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid #E6E9EE' }}>
            <Typography variant="subtitle2" sx={{ color: '#5F6B7A', fontWeight: 600, mb: 1.5 }}>
              {t(`${p}.sourcesTitle`)}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Link href="https://www.stm.info/en/info/fares/transit-fares" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t(`${p}.sourceSTM`)} <OpenInNew sx={{ fontSize: 14 }} />
              </Link>
              <Link href="https://www.hydroquebec.com/residential/customer-space/rates/rate-d.html" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t(`${p}.sourceHydro`)} <OpenInNew sx={{ fontSize: 14 }} />
              </Link>
              <Link href="https://www.revenuquebec.ca/en/" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Revenu Québec <OpenInNew sx={{ fontSize: 14 }} />
              </Link>
              <Link href="https://www.cmhc-schl.gc.ca/professionals/housing-markets-data-and-research/housing-data/data-tables/rental-market/rental-market-report-data-tables" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t(`${p}.sourceCMHC`)} <OpenInNew sx={{ fontSize: 14 }} />
              </Link>
            </Box>
            <Typography variant="caption" sx={{ color: '#5F6B7A', display: 'block', mt: 2 }}>
              {t(`${p}.updated`)}
            </Typography>
          </Box>

          {/* E-E-A-T Author Block */}
          <Divider sx={{ mt: 5, mb: 0 }} />
          <Box sx={{ mt: 3 }}>
            {/* Author */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, p: 3, bgcolor: '#F7F9FC', borderRadius: '12px', mb: 2 }}>
              <Box component="img" src={AUTHOR.photo} alt={AUTHOR.name} loading="lazy" sx={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3 }}>
                  {`${AUTHOR.name}, ${AUTHOR.credential}`}
                </Typography>
                <Typography variant="body2" sx={{ color: '#5F6B7A' }}>{`${t(`${p}.authorLabel`)} · ${AUTHOR.role}`}</Typography>
              </Box>
              <Button href={AUTHOR.linkedin} target="_blank" rel="noopener noreferrer" startIcon={<LinkedIn />} size="small"
                sx={{ textTransform: 'none', color: '#1E3A5F', fontWeight: 600, borderColor: '#1E3A5F', '&:hover': { bgcolor: 'rgba(10,102,194,0.08)' } }}
                variant="outlined">
                LinkedIn
              </Button>
            </Box>

            {/* Editor */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, p: 3, bgcolor: '#F7F9FC', borderRadius: '12px', mb: 2 }}>
              <Box component="img" src={EDITOR.photo} alt={EDITOR.name} loading="lazy" sx={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3 }}>
                  {`${EDITOR.name}, ${EDITOR.credential}`}
                </Typography>
                <Typography variant="body2" sx={{ color: '#5F6B7A' }}>{`${t(`${p}.editorLabel`)} · ${EDITOR.role}`}</Typography>
              </Box>
              <Button href={EDITOR.linkedin} target="_blank" rel="noopener noreferrer" startIcon={<LinkedIn />} size="small"
                sx={{ textTransform: 'none', color: '#1E3A5F', fontWeight: 600, borderColor: '#1E3A5F', '&:hover': { bgcolor: 'rgba(10,102,194,0.08)' } }}
                variant="outlined">
                LinkedIn
              </Button>
            </Box>

            {/* Disclaimer */}
            <Typography variant="caption" sx={{ color: '#5F6B7A', display: 'block', mt: 1, lineHeight: 1.6 }}>
              {t(`${p}.disclaimer`)}
            </Typography>
          </Box>
        </Box>

        <RelatedArticles slug="costo-de-vida-montreal-2026" />
      </Container>

      <NewsletterSection />
    </Box>
  );
};

export default CostoVidaMontrealPage;
